import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ApiLink from "../api/ApiLink";
import { useSelector } from "react-redux";
import { useSelectedCompany } from "../context/apiContext/SelectedCompanyContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function EstimatedQuarterlyWaterSavings() {
  const email = useSelector((state) => state.auth.email);
  const password = useSelector((state) => state.auth.password);
  const { selectedCompanyName } = useSelectedCompany();
  const [chartsData, setChartsData] = useState([]);
  const [quarterlyData, setQuarterlyData] = useState([]);

  useEffect(() => {
    const fetchChartsData = async () => {
      try {
        const response = await ApiLink.post("/api/get-charts-data", {
          email: email,
          password: password,
          company_id: selectedCompanyName.company_id,
        });
        console.log("Get charts data", response.data.Data);
        if (response.status === 200) {
          setChartsData(response.data.Data);
        }
      } catch (error) {
        console.error("Error:", "There is currently no charts data available");
      }
    };
    fetchChartsData();
  }, [email, password, selectedCompanyName]);

  useEffect(() => {
    if (chartsData && Object.keys(chartsData).length > 0) {
      calculateQuarterlySavings();
    }
  }, [chartsData]);

  const calculateQuarterlySavings = () => {
    const quarterlyTotals = {};
    console.log("Calculating quarterly savings", chartsData);

    // Process each location's data
    Object.values(chartsData).forEach((location) => {
      const locationData = location.data || {};

      Object.entries(locationData).forEach(([date, data]) => {
        if (data && data.savings !== undefined) {
          const [year, month] = date.split("-");
          const quarter = getQuarter(parseInt(month));
          const quarterKey = `Q${quarter}-${year}`;

          if (!quarterlyTotals[quarterKey]) {
            quarterlyTotals[quarterKey] = {
              totalSavings: 0,
              totalSprinkled: 0,
              totalOpportunities: 0,
              locationCount: 0,
            };
          }

          // Calculate savings in gallons (assuming 100 gallons per watering opportunity)
          const savingsGallons = (data.savings / 100) * 100; // Convert percentage to gallons
          quarterlyTotals[quarterKey].totalSavings += savingsGallons;
          quarterlyTotals[quarterKey].totalSprinkled += data.x || 0;
          quarterlyTotals[quarterKey].totalOpportunities += data.y || 0;
          quarterlyTotals[quarterKey].locationCount += 1;
        }
      });
    });

    console.log("Quarterly totals after processing:", quarterlyTotals);

    // Create the quarters in the EXACT order we want
    const orderedQuarters = [
      "Q3-2024",
      "Q4-2024",
      "Q1-2025",
      "Q2-2025",
      //"Q3-2025",
    ];

    // Convert to array format for chart with proper labels
    const quarterlyArray = orderedQuarters.map((quarterKey) => {
      const [q, year] = quarterKey.split("-");

      // Create proper quarter labels
      let quarterLabel = "";
      if (q === "Q1") {
        quarterLabel = `Jan-Mar-${q}-${year}`;
      } else if (q === "Q2") {
        quarterLabel = `Apr-Jun-${q}-${year}`;
      } else if (q === "Q3") {
        quarterLabel = `July-Sept-${q}-${year}`;
      } else if (q === "Q4") {
        quarterLabel = `Oct-Dec-${q}-${year}`;
      }

      // Get data for this quarter or use 0 if no data
      const quarterData = quarterlyTotals[quarterKey] || {
        totalSavings: 0,
        totalSprinkled: 0,
        totalOpportunities: 0,
        locationCount: 0,
      };

      return {
        quarter: quarterLabel,
        savings: Math.round(quarterData.totalSavings),
        sprinkled: quarterData.totalSprinkled,
        opportunities: quarterData.totalOpportunities,
        locationCount: quarterData.locationCount,
      };
    });

    console.log("Final quarterly array:", quarterlyArray);
    setQuarterlyData(quarterlyArray);
  };

  const getQuarter = (month) => {
    if (month >= 1 && month <= 3) return 1;
    if (month >= 4 && month <= 6) return 2;
    if (month >= 7 && month <= 9) return 3;
    if (month >= 10 && month <= 12) return 4;
    return 1;
  };

  const data = {
    labels: quarterlyData.map((q) => q.quarter),
    datasets: [
      {
        label: "Water Savings (Gallons)",
        data: quarterlyData.map((q) => q.savings),
        backgroundColor: "rgba(98, 203, 48, 0.8)",
        borderColor: "rgba(98, 203, 48, 1)",
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 60,
        maxBarThickness: 80,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: "Estimated Quarterly Water Savings",
        font: {
          size: 18,
          weight: "bold",
        },
        color: "gray",
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const quarter = quarterlyData[context.dataIndex];
            if (quarter) {
              return [
                `Quarter: ${quarter.quarter}`,
                `Water Savings: ${quarter.savings} gallons`,
                `Sprinkled: ${quarter.sprinkled} out of ${quarter.opportunities} times`,
                `Locations: ${quarter.locationCount}`,
              ];
            }
            return context.dataset.label + ": " + context.parsed.y;
          },
        },
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        titleFont: {
          size: 13,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Water Savings (Gallons)",
          font: {
            size: 12,
          },
        },
        ticks: {
          font: {
            size: 10,
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
          lineWidth: 0.5,
        },
      },
      x: {
        ticks: {
          font: {
            size: 10,
          },
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        top: 20,
        bottom: 10,
        left: 20,
        right: 20,
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 60,
        maxBarThickness: 80,
      },
    },
    interaction: {
      intersect: true,
      mode: "nearest",
    },
  };

  return (
    <div className="chart-container w-full" style={{ height: "400px" }}>
      <Bar data={data} options={options} />
    </div>
  );
}
